/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {createJob} from '@ion/core/src/services/Connect';
import {CreateJob} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export interface UseCreateJobParams {
  job: CreateJob
};

export type UseCreateJobResult = UseDefaultQueryResult & {
  save: ({job}: UseCreateJobParams) => void;
};

const api = async({job}: UseCreateJobParams) => {
  try {
    return await createJob({job: job});
  } catch (err) {
    throw new Error(err);
  }
};

/**
 * The useCreateJob hook is a generic hook that create a job (update the vbus status)
 * for existing device.
 *
 * ```typescript
 *
 *    import useCreateJob from '@ion/query/src/hooks/';
 *
 *    const {save, isSuccess, isError} = useCreateJob(deviceId);
 *
 * ```
 */
export const useCreateJob = (deviceId?: number): UseCreateJobResult => {
  const queryClient = useQueryClient();
  const mutation    = useMutation(api, {
    onSuccess : () => {
      if (deviceId) {
        queryClient.invalidateQueries([QueryKey.CREATE_JOB, deviceId]);
      }
    }
  });
  const save = useCallback(({job}: UseCreateJobParams) => {
    mutation.mutate({job: job});
  }, [mutation]);
  return createMutationResult(
    mutation, {save}
  ) as UseCreateJobResult;
};
export default useCreateJob;
